import { QueryHookOptions, useLazyQuery, useQuery } from '@apollo/client';
import useCurrentLoggedInUser from 'hooks/apollo/queries/useCurrentLoggedInUser';
import { flatten, isEmpty } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { GET_MESSAGES, GET_MESSAGES_TO_TIME } from 'services/graphql/queries/message.query';
import { NEW_MESSAGE_SUB } from 'services/graphql/subscriptions/message.subscription';
import { IMessage } from 'types/messages';
import { IGraphQLResponseData } from 'utils/types';

export interface IReactiveQuerySelectProps {
  // graphQLQuery: DocumentNode;
  // optionMapper?: GeneralFunction;
  queryProps?: QueryHookOptions;
  userId?: string;
  chatRoomId?: string;
}

export type WithKey<K extends string | number | symbol, ValueType> = {
  [k in K]: ValueType;
};

export default function useInfiniteMessageQuery({ queryProps = {}, userId, chatRoomId }: IReactiveQuerySelectProps) {
  const recordTaken = useMemo(() => queryProps?.variables?.pagination?.take || 20, [
    queryProps?.variables?.pagination?.take,
  ]);

  const firstMessageRef = useRef<IMessage>();

  const [oldMessagesState, setOldMessagesState] = useState<IMessage[][]>([]);

  // const [temporalDataState, setTemporalDataState] = useState<T[]>([]);
  // const [temporalMetaState, setTemporalMetaState] = useState<IPagination>();
  const userIdFromRedux = useCurrentLoggedInUser().id;
  const userIdData = userId || userIdFromRedux;

  const [firstLoading, setFirstLoading] = useState<boolean | undefined>(undefined);

  const { loading, data: queryResult, subscribeToMore, refetch } = useQuery<{
    getMessages: IGraphQLResponseData<IMessage>;
  }>(GET_MESSAGES, {
    // notifyOnNetworkStatusChange: true,
    ...queryProps,
    variables: {
      ...queryProps?.variables,
      chatRoom: chatRoomId,
      pagination: {
        page: 1,
        take: recordTaken,
        ...(queryProps?.variables?.pagination || {}),
      },
    },
    skip: queryProps.skip || !chatRoomId,
  });

  const { data, meta } = useMemo<Partial<IGraphQLResponseData<IMessage>>>(() => queryResult?.getMessages || {}, [
    queryResult,
  ]);

  const [
    getOldMessages,
    { loading: oldMessageLoading, data: oldMessageResult, refetch: refetchOldMessage },
  ] = useLazyQuery<{
    getMessagesToTime: IGraphQLResponseData<IMessage>;
  }>(GET_MESSAGES_TO_TIME, {
    notifyOnNetworkStatusChange: true,
    ...queryProps,
    variables: {
      ...queryProps?.variables,
      chatRoom: chatRoomId,
      toTime: firstMessageRef.current?.createdAt,
      pagination: {
        page: 1,
        take: recordTaken,
        ...(queryProps?.variables?.pagination || {}),
      },
    },
  });

  const { data: oldMessageData, meta: oldMessageMeta } = useMemo<Partial<IGraphQLResponseData<IMessage>>>(
    () => oldMessageResult?.getMessagesToTime || {},
    [oldMessageResult],
  );

  useEffect(() => {
    if (chatRoomId) {
      refetch();
      refetchOldMessage?.();
    }
  }, [chatRoomId]);

  useEffect(() => {
    if (oldMessageData !== undefined && oldMessageMeta !== undefined) {
      setOldMessagesState((c) => {
        const current = [...c];
        if (oldMessageMeta.page !== undefined) current[oldMessageMeta.page - 1] = oldMessageData;
        return current;
      });
    }
  }, [oldMessageMeta, oldMessageData]);

  const fetchOldMessages = () => {
    if (!oldMessageResult) {
      getOldMessages();
      return;
    }

    if (oldMessageMeta?.hasNextPage && !oldMessageLoading) {
      getOldMessages({
        variables: {
          pagination: { page: (oldMessageMeta?.page || 0) + 1, take: 20, ...(queryProps?.variables?.pagination || {}) },
        },
      });
    }
  };

  const subscribeToMessages = (chatRoomIdToCheck: string) => {
    if (!chatRoomIdToCheck) return () => {};
    return subscribeToMore<{ subMessages: IMessage[] }>({
      document: NEW_MESSAGE_SUB,
      variables: { id: userIdData },
      updateQuery: (prev, { subscriptionData }) => {
        const newMessageResponse = subscriptionData.data.subMessages?.[0];
        const existingMessages = prev.getMessages;
        const hasNewMessageExist = existingMessages.data.some((m) => m.id === newMessageResponse.id);
        const newMessage = newMessageResponse.chatRoom?.id === chatRoomIdToCheck ? [newMessageResponse] : [];
        return isEmpty(prev) || isEmpty(newMessage) || hasNewMessageExist
          ? prev
          : { ...prev, getMessages: { ...existingMessages, data: [...newMessage, ...existingMessages.data] } };
      },
    });
  };

  useEffect(() => {
    if (!firstMessageRef.current && data) {
      firstMessageRef.current = data?.[data?.length - 1];
    }
  }, [data]);

  useEffect(() => {
    if (firstLoading === undefined && loading) {
      setFirstLoading(true);
    } else if (firstLoading) {
      setFirstLoading(false);
    }
  }, [loading]);

  // console.log('tesstttt ================>', flatten([data, ...oldMessagesState]), oldMessageMeta);
  const oldMessageDataByFlattenedState = useMemo(() => flatten(oldMessagesState), [oldMessagesState]);
  return {
    loading,
    // meta: temporalMetaState || metaState,
    // data: flattenedData,
    data,
    meta,
    oldMessageData: oldMessageDataByFlattenedState,
    oldMessageMeta,
    oldMessageLoading,
    firstLoading,
    subscribeToMessages,
    fetchOldMessages,
    refetch,
  };
}
